import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { toast, useToast } from "@shared/ui/use-toast";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import {
  axSharedApi,
  deleteDocument,
  getAssetCondition,
  getConditionByType,
  getElectricUnit,
} from "./api";

export const useCreateNote = () => {
  return useSmartMutation((payload: any) => axSharedApi.createNote(payload), {
    onSuccess: () => {
      toast({
        title: "Note created successfully!",
        variant: "default",
      });

      const key = QueryKeyFactory.module.ax.shared.notesList().slice(0, 4);

      queryClient.invalidateQueries({
        queryKey: key,
      });
    },
    onError: (error: Error) => {
      toast({
        title: `Failed to create a note: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useGetNotes = (id: string, type: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.shared.notesList(id, type),
    () => axSharedApi.getNotes(id, type)
  );
};

export const useGetActivityLog = (id: string, type: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.shared.activityLogList(id, type),
    () => axSharedApi.getActivityLog(id, type)
  );
};

export const usePriorityOptions = () => {
  return useSmartQuery(QueryKeyFactory.module.ax.shared.priorityOptions(), () =>
    axSharedApi.getPriorityOptions()
  );
};

export const useAssetStatusList = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.shared.assetStatusOptions(),
    () => axSharedApi.getAssetStatusList()
  );
};

export const useIncidentStatusList = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.shared.assetStatusOptions(),
    () => axSharedApi.getIncidentStatusList()
  );
};

export const useFacilitiesList = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.shared.facilitiesOptions(),
    () => axSharedApi.getFacilitiesList()
  );
};

export const useNetworkOptions = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.shared.networkListOptions(),
    () => axSharedApi.getNetworksList()
  );
};

export const useSystemOptions = (facilityId?: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.shared.systemOptions(facilityId),
    () => axSharedApi.getAllSystems(facilityId)
  );
};

export const useCalculateCompliance = () => {
  return useSmartMutation((payload: any) =>
    axSharedApi.calculateCompliance(payload)
  );
};

export const useAssessCalculation = (assessmentName: string, id: number) => {
  const { toast } = useToast();
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartMutation(
    (payload: any) => axSharedApi.assessCalculation(payload),
    {
      onSuccess: (_data, payload) => {
        if (payload.isAccessCondition) {
          toast({
            title: "Assess Calculated",
            description: _data?.message,
          });
        } else {
          toast({
            title: "Assess Calculated",
            description: _data?.message,
          });
        }
        queryClient.refetchQueries({
          queryKey:
            QueryKeyFactory.module.ax.facilities.getSystemConditionAndRiskById(
              assessmentName,
              id,
              remoteUtilityId
            ),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.facilities
            .getConditionHistory(id)
            .slice(0, 4),
        });
      },
      onError: (e) => {
        toast({ title: e?.message, variant: "destructive" });
      },
    }
  );
};

export const usegetConditionHistory = (
  id: number,
  entityType: number,
  page?: number
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.getConditionHistory(
      id,
      entityType,
      page
    ),
    () => axSharedApi.getConditionHistory(id, entityType, page),
    {
      enabled: !!id,
    }
  );
};

export const useAssetType = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.shared.assetTypeOptions(),
    () => axSharedApi.getAssetType()
  );
};

export const useNetworkTypeOptions = () => {
  return useSmartQuery(QueryKeyFactory.module.ax.shared.networkOptions(), () =>
    axSharedApi.getNetworksTypeOptions()
  );
};

export const useDeleteDocument = (type: string) => {
  return useSmartMutation((id: string) => deleteDocument(id), {
    onSuccess: () => {
      toast({
        title: "Document removed successfully!",
        variant: "default",
      });
      switch (type) {
        case "asset":
          queryClient.refetchQueries({
            queryKey: QueryKeyFactory.module.ax.assets
              .assetDetail()
              .slice(0, 4),
          });
          break;

        case "facility":
          queryClient.refetchQueries({
            queryKey: QueryKeyFactory.module.ax.facilities.list().slice(0, 3),
          });
          break;

        case "system":
          queryClient.refetchQueries({
            queryKey: QueryKeyFactory.module.ax.systemManagement
              .getSystemDataById()
              .slice(0, 3),
          });
          break;
        case "network":
          queryClient.refetchQueries({
            queryKey: QueryKeyFactory.module.ax.networks
              .documents()
              .slice(0, 2),
          });
          break;
        default:
          console.warn(`Unknown delete type: ${type}`);
      }
    },
    onError: (error: Error) => {
      toast({
        title: `Failed to delete a document: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};

export const useSystemTypeOptions = () => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.shared.systemTypeOptions(),
    () => axSharedApi.getSystemTypeOptions(remoteUtilityId),
    {
      enabled: Boolean(remoteUtilityId),
    }
  );
};

export const useFacilityTypeOptions = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.shared.facilityTypeOptions(),
    () => axSharedApi.getFacilityTypeOptions()
  );
};

export const useNetworkChoices = () => {
  return useSmartQuery(QueryKeyFactory.module.ax.shared.networkChoices(), () =>
    axSharedApi.getNetworkChoices()
  );
};

export const useGetAllSchedules = (
  entityId: string,
  entityType: string,
  page: number,
  limit: number,
  search?: string,
  status?: number,
  system_type?: string,
  criticality?: string
) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.shared.getAllSchedules(
      entityId,
      entityType,
      remoteUtilityId,
      page,
      limit,
      search,
      status,
      system_type,
      criticality
    ),
    () =>
      axSharedApi.getAllSchedules(
        entityId,
        entityType,
        remoteUtilityId,
        page,
        limit,
        search,
        status,
        system_type,
        criticality
      ),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useJobcardStatusChoices = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.shared.jobcardStatusChoices(),
    () => axSharedApi.getJobcardStatusChoices()
  );
};

export const useAssetCondition = () => {
  return useSmartQuery(QueryKeyFactory.module.ax.shared.assetCondition(), () =>
    getAssetCondition()
  );
};

export const useConditionByType = (className: string, typeCode: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.shared.conditionList(className, typeCode),
    () => getConditionByType(className, typeCode),
    {
      enabled: Boolean(className && typeCode),
    }
  );
};

export const useUnitChoices = () => {
  return useSmartQuery(QueryKeyFactory.module.ax.shared.unitChoices(), () =>
    axSharedApi.getUnitOptions()
  );
};

export const useElectricUnit = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.shared.electricUnitChoices(),
    () => getElectricUnit()
  );
};

export const useFilterEntities = (
  entityId: string,
  entityType: string,
  filterType: string,
  page: number,
  limit: number
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.shared.filteredEntities(
      entityId,
      entityType,
      filterType,
      page,
      limit
    ),
    () =>
      axSharedApi.getFilterEntities(
        entityId,
        entityType,
        filterType,
        page,
        limit
      )
  );
};
